import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart3, Hash, DollarSign, Percent, RefreshCw, Loader2,
  Search, ArrowUpDown, ChevronUp, ChevronDown, Building2,
  AlertCircle, AlertTriangle, CheckCircle2, RotateCcw, Send,
  ChevronRight, ChevronLeft, Check, Minus, Plus, ArrowRight,
  Eye, ListChecks, Layers, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import stockAdjustmentService from '../../../../services/stockAdjustmentService';
import branchService from '../../../../services/branchService';
import { useAuth } from '../../../../context/AuthContext';
import { P } from '@/config/permissionCodes';
import useStockAdjustmentStats, { useInventoryItemsForAdjustment, useSubmitAdjustments,useAdjustmentHistory, } from '@/hooks/useStockAdjustmentStats';

// ── Constants ─────────────────────────────────────────────────────────────────
const REASON_OPTIONS = [
  { value: 'counting_error', label: 'Counting Error' },
  { value: 'shrinkage',      label: 'Shrinkage' },
  { value: 'damage',         label: 'Damage' },
  { value: 'transfer_in',    label: 'Transfer In' },
  { value: 'transfer_out',   label: 'Transfer Out' },
  { value: 'other',          label: 'Other' },
];

const REASON_BADGE_COLORS = {
  counting_error: 'bg-blue-100 text-blue-700',
  shrinkage:      'bg-red-100 text-red-700',
  damage:         'bg-amber-100 text-amber-700',
  transfer_in:    'bg-green-100 text-green-700',
  transfer_out:   'bg-violet-100 text-violet-700',
  other:          'bg-slate-100 text-slate-700',
};

const REASON_LABELS = Object.fromEntries(REASON_OPTIONS.map((r) => [r.value, r.label]));
const STEP_LABELS   = ['Select Items', 'Count Stock', 'Review & Submit'];

// ── Reusable Components ───────────────────────────────────────────────────────
function SummaryCard({ icon: Icon, title, value, subtitle, color, iconBg, iconColor }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon size={24} className={iconColor} />
        </div>
      </div>
    </div>
  );
}

// ── Access Denied ─────────────────────────────────────────────────────────────
function AccessDenied() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view Stock Adjustments.</p>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function StockAdjustmentList() {
  const { user, hasPermission } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';

  // ── Permissions ──────────────────────────────────────────────────────────────
  const canViewScreen = hasPermission(P.INVENTORY.STOCK_ADJUSTMENT.SCREEN);
  const canAdjust     = hasPermission(P.INVENTORY.STOCK_ADJUSTMENT.ADJUST);

  if (!canViewScreen) return <AccessDenied />;

  // History filters
const [historySearch, setHistorySearch]     = useState('');
const [historyReason, setHistoryReason]     = useState('');
const [historyFromDate, setHistoryFromDate] = useState(() => {
  const today = new Date();
  const day = today.getDay();
  const diff = today.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(today.setDate(diff)).toISOString().split('T')[0];
});
const [historyToDate, setHistoryToDate] = useState(() => {
  return new Date().toISOString().split('T')[0];
});
const [historyPeriod, setHistoryPeriod] = useState('today');
const [sortConfig, setSortConfig]           = useState({ key: 'createdAt', direction: 'desc' });

  // ── State ─────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]               = useState('count');
  const [isLoading, setIsLoading]               = useState(true);
  const [branches, setBranches]                 = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  // const [stats, setStats]                       = useState({ totalAdjustments: 0, totalItemsAdjusted: 0, netAdjustmentValue: 0, avgVariancePercent: 0 });
  const { stats, refetch: refetchStats } = useStockAdjustmentStats(
  selectedBranchId,
  historyPeriod ? null : historyFromDate,
  historyPeriod ? null : historyToDate,
  historyPeriod || null,
);
  const { inventoryItems, refetch: refetchItems } = useInventoryItemsForAdjustment(selectedBranchId);
  const { history, refetch: refetchHistory } = useAdjustmentHistory({
  search:   historySearch,
  reason:   historyReason,
  fromDate: historyFromDate,
  toDate:   historyToDate,
  period:   historyPeriod,
});



  // Wizard state
  const [wizardStep, setWizardStep]                   = useState(0);
  const [selectedItemIds, setSelectedItemIds]         = useState(new Set());
  const [countData, setCountData]                     = useState({});
  const { submitAdjustments, isSubmitting } = useSubmitAdjustments();
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  // ── Effects ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches()
      .then((r) => { if (r?.length > 1) setBranches(r); })
      .catch(console.error);
  }, [isOwner]);


  // ── Derived data ──────────────────────────────────────────────────────────────
  const categories = useMemo(() => [...new Set(inventoryItems.map((i) => i.categoryName))].sort(), [inventoryItems]);

  const itemsByCategory = useMemo(() => {
    const map = {};
    inventoryItems.forEach((item) => {
      if (!map[item.categoryName]) map[item.categoryName] = [];
      map[item.categoryName].push(item);
    });
    return map;
  }, [inventoryItems]);

  const selectedItems = useMemo(() => inventoryItems.filter((i) => selectedItemIds.has(i.id)), [inventoryItems, selectedItemIds]);

  const countedCount = useMemo(() =>
    selectedItems.filter((i) => countData[i.id]?.actualCount !== undefined && countData[i.id]?.actualCount !== '').length,
    [selectedItems, countData]
  );

  const changedItems = useMemo(() =>
    selectedItems.filter((item) => {
      const d = countData[item.id];
      if (!d || d.actualCount === undefined || d.actualCount === '') return false;
      return d.actualCount !== item.systemStock;
    }),
    [selectedItems, countData]
  );

  const reviewSummary = useMemo(() => {
    let totalPositive = 0, totalNegative = 0, itemsWithVariance = 0;
    changedItems.forEach((item) => {
      const d = countData[item.id];
      const variance = d.actualCount - item.systemStock;
      const cost = Math.round(variance * item.unitCost);
      if (cost > 0) totalPositive += cost; else totalNegative += cost;
      if (variance !== 0) itemsWithVariance++;
    });
    return { totalPositive, totalNegative, netCost: totalPositive + totalNegative, itemsWithVariance, totalChanged: changedItems.length };
  }, [changedItems, countData]);

  // History filtering
  // const filteredHistory = useMemo(() => {
  //   let result = [...history];
  //   if (historySearch) {
  //     const q = historySearch.toLowerCase();
  //     result = result.filter((h) => h.itemName.toLowerCase().includes(q) || h.adjustedBy.toLowerCase().includes(q));
  //   }
  //   if (historyReason)   result = result.filter((h) => h.reason === historyReason);
  //   if (historyFromDate) result = result.filter((h) => h.createdAt >= historyFromDate);
  //   if (historyToDate)   result = result.filter((h) => h.createdAt <= historyToDate + 'T23:59:59Z');
  //   result.sort((a, b) => {
  //     const aV = a[sortConfig.key], bV = b[sortConfig.key];
  //     const dir = sortConfig.direction === 'asc' ? 1 : -1;
  //     if (typeof aV === 'number') return (aV - bV) * dir;
  //     return String(aV).localeCompare(String(bV)) * dir;
  //   });
  //   return result;
  // }, [history, historySearch, historyReason, historyFromDate, historyToDate, sortConfig]);

  const sortedHistory = useMemo(() => {
  return [...history].sort((a, b) => {
    const aV = a[sortConfig.key], bV = b[sortConfig.key];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    if (typeof aV === 'number') return (aV - bV) * dir;
    return String(aV).localeCompare(String(bV)) * dir;
  });
}, [history, sortConfig]);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const toggleItem = (id) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleCategory = (cat) => {
    const catItems = itemsByCategory[cat] || [];
    const allSelected = catItems.every((i) => selectedItemIds.has(i.id));
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      catItems.forEach((i) => allSelected ? next.delete(i.id) : next.add(i.id));
      return next;
    });
  };

  const selectAll = () => setSelectedItemIds(new Set(inventoryItems.map((i) => i.id)));
  const clearAll  = () => setSelectedItemIds(new Set());

  const updateCount = (itemId, value) => {
    const numVal = value === '' ? '' : parseFloat(value);
    setCountData((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), actualCount: numVal === '' ? '' : numVal } }));
  };

  const stepCount = (itemId, systemStock, delta) => {
    setCountData((prev) => {
      const current = prev[itemId]?.actualCount;
      const base    = (current !== undefined && current !== '') ? current : systemStock;
      const newVal  = Math.max(0, Math.round((base + delta) * 100) / 100);
      return { ...prev, [itemId]: { ...(prev[itemId] || {}), actualCount: newVal } };
    });
  };

  const updateReason = (itemId, value) =>
    setCountData((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), reason: value } }));

  const updateNotes = (itemId, value) =>
    setCountData((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), notes: value } }));

  const handleSubmit = async () => {
    if (!canAdjust) return;
    const adjustments = [];
    let missingReason = false;
    changedItems.forEach((item) => {
      const d = countData[item.id];
      if (!d.reason) { missingReason = true; return; }
      adjustments.push({ inventoryItemId: item.id, actualCount: d.actualCount, reason: d.reason, notes: d.notes || '' });
    });
    if (missingReason) { toast.error('Please select a reason for all changed items'); return; }
    if (!adjustments.length) { toast.error('No changes to submit'); return; }
try {
  await submitAdjustments(adjustments);
  toast.success(`${adjustments.length} item${adjustments.length > 1 ? 's' : ''} adjusted successfully`);
  resetWizard();
  await Promise.all([refetchHistory(), refetchStats(), refetchItems()]);
} catch {
  toast.error('Failed to submit adjustments');
}
  };

  const resetWizard = () => {
    setWizardStep(0);
    setSelectedItemIds(new Set());
    setCountData({});
    setCollapsedCategories(new Set());
  };

  const handleSort = (key) =>
    setSortConfig((p) => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ArrowUpDown size={14} className="text-slate-400" />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp size={14} className="text-teal-600" />
      : <ChevronDown size={14} className="text-teal-600" />;
  };

  const toggleCollapse = (cat) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  // ── Loading ───────────────────────────────────────────────────────────────────
  // if (isLoading) {
  //   return (
  //     <div className="flex flex-col h-full bg-slate-50 -m-6">
  //       <div className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10">
  //         <h1 className="text-lg font-semibold text-slate-900">Stock Adjustments</h1>
  //       </div>
  //       <div className="flex-1 flex items-center justify-center">
  //         <div className="text-center">
  //           <Loader2 size={40} className="text-teal-500 animate-spin mx-auto mb-4" />
  //           <p className="text-slate-500">Loading inventory data...</p>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

    // ── Date range presets ────────────────────────────────────────────
const getDateRange = (preset) => {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  switch (preset) {
    case 'today':
      return { from: fmt(today), to: fmt(today) };
    case 'yesterday': {
      const y = new Date(today); y.setDate(today.getDate() - 1);
      return { from: fmt(y), to: fmt(y) };
    }
    case 'this_week': {
      const day = today.getDay();
      const mon = new Date(today); mon.setDate(today.getDate() - day + (day === 0 ? -6 : 1));
      return { from: fmt(mon), to: fmt(today) };
    }
    case 'this_month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(first), to: fmt(today) };
    }
    case 'last_month': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last  = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: fmt(first), to: fmt(last) };
    }
    default: return null;
  }
};

const applyPreset = (preset) => {
  const range = getDateRange(preset);
  if (range) { setHistoryFromDate(range.from); setHistoryToDate(range.to); setHistoryPeriod(preset); }
};

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-slate-50 -m-6">

      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 sticky top-0 z-10">

{/* ROW 1 */}
<div className="flex items-center justify-between py-3">

  {/* LEFT SIDE */}
  <div className="flex items-center gap-3">

    <h1 className="text-lg font-semibold text-slate-900">
      Stock Adjustments
    </h1>

    <div className="flex items-center bg-slate-100 rounded-lg p-0.5">

      {canAdjust && (
        <button
          onClick={()=>setActiveTab('count')}
          className={`px-3 py-1.5 text-sm rounded-md ${
            activeTab==='count'
            ? 'bg-white text-teal-700 border border-slate-200'
            : 'text-slate-600'
          }`}
        >
          Stock Count
        </button>
      )}

      <button
        onClick={()=>setActiveTab('history')}
        className={`px-3 py-1.5 text-sm rounded-md ${
          activeTab==='history'
          ? 'bg-white text-teal-700 border border-slate-200'
          : 'text-slate-600'
        }`}
      >
        Adjustment History
      </button>

    </div>

  </div>


  {/* RIGHT SIDE */}
  <div className="flex items-center gap-2">

    {branches.length > 1 && (

      <div className="relative">

        <Building2
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />

        <select
          value={selectedBranchId ?? ''}
          onChange={(e)=>setSelectedBranchId(e.target.value||null)}
          className="h-9 pl-9 pr-8 text-sm border border-slate-200 rounded-lg"
        >
          <option value="">All Branches</option>

          {branches.map(b=>(
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}

        </select>

        <ChevronDown
          size={14}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
        />

      </div>

    )}

    <button
      onClick={()=>{
        refetchHistory();
        refetchStats();
        refetchItems();
      }}
      className="h-9 w-9 flex items-center justify-center border border-slate-200 rounded-lg hover:bg-slate-100"
    >
      <RefreshCw size={15}/>
    </button>

  </div>

</div>



{/* ROW 2 */}
<div className="flex items-center gap-3 pb-3">

  {/* Presets */}
  <div className="flex items-center bg-slate-100 rounded-lg p-0.5">

    {[
      {label:'Today',key:'today'},
      {label:'Yesterday',key:'yesterday'},
      {label:'This Week',key:'this_week'},
      {label:'This Month',key:'this_month'},
      {label:'Last Month',key:'last_month'}
    ].map(({label,key})=>(

      <button
        key={key}
        onClick={()=>applyPreset(key)}
        className={`px-3 py-1 text-xs rounded-md ${
          historyPeriod===key
          ? 'bg-white text-teal-700 border border-slate-200'
          : 'text-slate-600'
        }`}
      >
        {label}
      </button>

    ))}

  </div>


  <div className="h-5 w-px bg-slate-200"/>


  {/* Date */}
  <div className="flex items-center gap-2">

    <input
      type="date"
      value={historyFromDate}
      onChange={(e)=>{
        setHistoryFromDate(e.target.value);
        setHistoryPeriod('');
      }}
      className="h-9 px-3 text-sm border border-slate-200 rounded-lg"
    />

    <span className="text-slate-400">
      →
    </span>

    <input
      type="date"
      value={historyToDate}
      onChange={(e)=>{
        setHistoryToDate(e.target.value);
        setHistoryPeriod('');
      }}
      className="h-9 px-3 text-sm border border-slate-200 rounded-lg"
    />

  </div>

</div>

</div>

      <div className="flex-1 overflow-y-auto p-6">

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard icon={BarChart3} title="Total Adjustments"    value={stats.totalAdjustments}  subtitle="All time"          color="text-slate-900"   iconColor="text-teal-600"   iconBg="bg-teal-100" />
          <SummaryCard icon={Hash}      title="Items Adjusted"       value={stats.totalItemsAdjusted} subtitle="Unique items"       color="text-slate-900"   iconColor="text-emerald-600" iconBg="bg-emerald-100" />
          <SummaryCard
            icon={DollarSign}
            title="Net Adjustment Value"
            value={`₹${Math.abs(stats.netAdjustmentValue).toLocaleString('en-IN')}`}
            subtitle={stats.netAdjustmentValue >= 0 ? 'Net positive' : 'Net shrinkage'}
            color={stats.netAdjustmentValue >= 0 ? 'text-emerald-600' : 'text-red-600'}
            iconColor={stats.netAdjustmentValue >= 0 ? 'text-amber-600' : 'text-red-600'}
            iconBg={stats.netAdjustmentValue >= 0 ? 'bg-amber-100' : 'bg-red-100'}
          />
          <SummaryCard icon={Percent} title="Avg Variance " value={`${Number(stats.avgVariancePercent).toFixed(2)}%`} subtitle="Average deviation" color="text-slate-900" iconColor="text-slate-600" iconBg="bg-slate-100" />
        </div>

        {/* Tab Content */}
        {activeTab === 'count' && canAdjust ? (
          <StockCountWizard
            step={wizardStep} setStep={setWizardStep}
            inventoryItems={inventoryItems} categories={categories} itemsByCategory={itemsByCategory}
            selectedItemIds={selectedItemIds} toggleItem={toggleItem} toggleCategory={toggleCategory}
            selectAll={selectAll} clearAll={clearAll}
            selectedItems={selectedItems} countData={countData} countedCount={countedCount}
            updateCount={updateCount} stepCount={stepCount} updateReason={updateReason} updateNotes={updateNotes}
            changedItems={changedItems} reviewSummary={reviewSummary}
            onSubmit={handleSubmit} onReset={resetWizard} isSubmitting={isSubmitting}
            collapsedCategories={collapsedCategories} toggleCollapse={toggleCollapse}
          />
        ) : activeTab === 'history' ? (
          <AdjustmentHistoryTab
            history={sortedHistory}
            search={historySearch} setSearch={setHistorySearch}
            reason={historyReason} setReason={setHistoryReason}
            // fromDate={historyFromDate} setFromDate={setHistoryFromDate}
            // toDate={historyToDate} setToDate={setHistoryToDate}
            onSort={handleSort} SortIcon={SortIcon}
          />
        ) : (
          /* Fallback: user has screen access but not adjust — show history only */
          <AdjustmentHistoryTab
            history={sortedHistory}
            search={historySearch} setSearch={setHistorySearch}
            reason={historyReason} setReason={setHistoryReason}
            // fromDate={historyFromDate} setFromDate={setHistoryFromDate}
            // toDate={historyToDate} setToDate={setHistoryToDate}
            onSort={handleSort} SortIcon={SortIcon}
          />
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// STOCK COUNT WIZARD  (unchanged — no permission props needed here)
// ══════════════════════════════════════════════════════════════════
function StockCountWizard({
  step, setStep, inventoryItems, categories, itemsByCategory,
  selectedItemIds, toggleItem, toggleCategory, selectAll, clearAll,
  selectedItems, countData, countedCount,
  updateCount, stepCount, updateReason, updateNotes,
  changedItems, reviewSummary, onSubmit, onReset, isSubmitting,
  collapsedCategories, toggleCollapse,
}) {
  return (
    <div className="space-y-4">
      {/* Stepper */}
      <div className="bg-white rounded-xl border border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          {STEP_LABELS.map((label, i) => (
            <React.Fragment key={i}>
              <button
                onClick={() => {
                  if (i < step || (i === 1 && selectedItemIds.size > 0) || (i === 2 && countedCount > 0)) setStep(i);
                }}
                className={`flex items-center gap-2.5 transition-all ${i <= step ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  i < step ? 'bg-teal-600 text-white' : i === step ? 'bg-teal-600 text-white ring-4 ring-teal-100' : 'bg-slate-100 text-slate-400'
                }`}>
                  {i < step ? <Check size={16} /> : i + 1}
                </div>
                <span className={`text-sm font-medium hidden sm:block ${i <= step ? 'text-slate-900' : 'text-slate-400'}`}>{label}</span>
              </button>
              {i < STEP_LABELS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-3 rounded transition-all ${i < step ? 'bg-teal-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {step === 0 && (
        <Step1_SelectItems
          categories={categories} itemsByCategory={itemsByCategory} inventoryItems={inventoryItems}
          selectedItemIds={selectedItemIds} toggleItem={toggleItem} toggleCategory={toggleCategory}
          selectAll={selectAll} clearAll={clearAll}
          onNext={() => setStep(1)} totalItems={inventoryItems.length}
        />
      )}
      {step === 1 && (
        <Step2_CountStock
          selectedItems={selectedItems} countData={countData} countedCount={countedCount}
          updateCount={updateCount} stepCount={stepCount}
          onBack={() => setStep(0)} onNext={() => setStep(2)} onReset={onReset}
          collapsedCategories={collapsedCategories} toggleCollapse={toggleCollapse}
        />
      )}
      {step === 2 && (
        <Step3_ReviewSubmit
          changedItems={changedItems} countData={countData} reviewSummary={reviewSummary}
          updateReason={updateReason} updateNotes={updateNotes}
          onBack={() => setStep(1)} onSubmit={onSubmit} onReset={onReset}
          isSubmitting={isSubmitting} selectedItems={selectedItems}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// STEP 1: SELECT ITEMS — Category Sidebar + Search + Item Grid
// ══════════════════════════════════════════════════════════════════
function Step1_SelectItems({ categories, itemsByCategory, inventoryItems, selectedItemIds, toggleItem, toggleCategory, selectAll, clearAll, onNext, totalItems }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null); // null = All

  // Filter items by search + active category
  const displayItems = useMemo(() => {
    let items = activeCategory ? (itemsByCategory[activeCategory] || []) : inventoryItems;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter((i) => i.name.toLowerCase().includes(q) || i.categoryName.toLowerCase().includes(q));
    }
    return items;
  }, [activeCategory, itemsByCategory, inventoryItems, searchQuery]);

  // Group displayed items by category for rendering
  const displayedByCategory = useMemo(() => {
    const map = {};
    displayItems.forEach((item) => {
      if (!map[item.categoryName]) map[item.categoryName] = [];
      map[item.categoryName].push(item);
    });
    return map;
  }, [displayItems]);

  const displayedCategories = Object.keys(displayedByCategory).sort();

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-semibold text-slate-900">Select Items to Count</h2>
            <p className="text-sm text-slate-500 mt-0.5">Pick categories from the left, or search to find items quickly</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg">
              <span className="font-semibold text-teal-700">{selectedItemIds.size}</span> / {totalItems} selected
            </span>
            {selectedItemIds.size > 0 && (
              <button onClick={clearAll} className="px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <X size={12} className="inline mr-1 -mt-0.5" />
                Clear
              </button>
            )}
            <button onClick={selectAll} className="px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors border border-teal-200">
              <ListChecks size={14} className="inline mr-1 -mt-0.5" />
              Select All
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search items by name or category..."
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-slate-50"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Body: Category Sidebar + Items Grid */}
      <div className="flex" style={{ minHeight: '400px' }}>
        {/* Left Sidebar — Categories */}
        <div className="w-52 border-r border-slate-200 bg-slate-50/80 flex-shrink-0 overflow-y-auto">
          <div className="p-2">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 py-2">Categories</p>

            {/* All Items */}
            <button
              onClick={() => setActiveCategory(null)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left text-sm transition-all mb-0.5 ${
                activeCategory === null
                  ? 'bg-teal-600 text-white font-medium shadow-sm'
                  : 'text-slate-700 hover:bg-white hover:shadow-sm'
              }`}
            >
              <div className="flex items-center gap-2">
                <Layers size={14} />
                <span>All Items</span>
              </div>
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                activeCategory === null ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {totalItems}
              </span>
            </button>

            {/* Category List */}
            {categories.map((cat) => {
              const items = itemsByCategory[cat] || [];
              const selectedInCat = items.filter((i) => selectedItemIds.has(i.id)).length;
              const isActive = activeCategory === cat;

              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left text-sm transition-all mb-0.5 ${
                    isActive
                      ? 'bg-teal-600 text-white font-medium shadow-sm'
                      : 'text-slate-700 hover:bg-white hover:shadow-sm'
                  }`}
                >
                  <span className="truncate">{cat}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {selectedInCat > 0 && (
                      <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-white' : 'bg-teal-500'}`} />
                    )}
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {items.length}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Panel — Items Grid */}
        <div className="flex-1 overflow-y-auto">
          {displayedCategories.length === 0 ? (
            <div className="flex items-center justify-center h-full py-12">
              <div className="text-center">
                <Search size={32} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No items found</p>
                <p className="text-sm text-slate-400 mt-1">Try a different search term</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {displayedCategories.map((cat) => {
                const items = displayedByCategory[cat];
                const allCatItems = itemsByCategory[cat] || [];
                const selectedInCat = allCatItems.filter((i) => selectedItemIds.has(i.id)).length;
                const allSelected = selectedInCat === allCatItems.length;
                const someSelected = selectedInCat > 0 && !allSelected;

                return (
                  <div key={cat}>
                    {/* Category Group Header — only when viewing All */}
                    {!activeCategory && (
                      <div className="px-5 py-2.5 bg-slate-50 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <button
                            onClick={() => toggleCategory(cat)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              allSelected ? 'bg-teal-600 border-teal-600' : someSelected ? 'bg-teal-100 border-teal-400' : 'border-slate-300 hover:border-slate-400'
                            }`}
                          >
                            {allSelected && <Check size={12} className="text-white" />}
                            {someSelected && <Minus size={12} className="text-teal-600" />}
                          </button>
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{cat}</span>
                          <span className="text-[10px] text-slate-400">{allCatItems.length} items</span>
                        </div>
                        {selectedInCat > 0 && (
                          <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">{selectedInCat} selected</span>
                        )}
                      </div>
                    )}

                    {/* Select All for category when viewing specific category */}
                    {activeCategory && (
                      <div className="px-5 py-2.5 bg-slate-50 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <button
                            onClick={() => toggleCategory(cat)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              allSelected ? 'bg-teal-600 border-teal-600' : someSelected ? 'bg-teal-100 border-teal-400' : 'border-slate-300 hover:border-slate-400'
                            }`}
                          >
                            {allSelected && <Check size={12} className="text-white" />}
                            {someSelected && <Minus size={12} className="text-teal-600" />}
                          </button>
                          <span className="text-xs font-medium text-slate-600">Select all in {cat}</span>
                        </div>
                        <span className="text-[10px] text-slate-400">{selectedInCat}/{allCatItems.length} selected</span>
                      </div>
                    )}

                    {/* Item Cards — Table-like layout */}
                    <div className="px-5 py-3">
                      {/* Mini header for item list */}
                      <div className="grid grid-cols-12 gap-2 px-3 pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                        <div className="col-span-1"></div>
                        <div className="col-span-5">Item Name</div>
                        <div className="col-span-3 text-right">System Stock</div>
                        <div className="col-span-3 text-right">Unit Cost</div>
                      </div>
                      <div className="space-y-1">
                        {items.map((item) => {
                          const selected = selectedItemIds.has(item.id);
                          return (
                            <button
                              key={item.id}
                              onClick={() => toggleItem(item.id)}
                              className={`w-full grid grid-cols-12 gap-2 items-center px-3 py-2.5 rounded-lg border transition-all text-left ${
                                selected ? 'border-teal-300 bg-teal-50/60' : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                              }`}
                            >
                              <div className="col-span-1">
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                  selected ? 'bg-teal-600 border-teal-600' : 'border-slate-300'
                                }`}>
                                  {selected && <Check size={10} className="text-white" />}
                                </div>
                              </div>
                              <div className="col-span-5">
                                <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                              </div>
                              <div className="col-span-3 text-right">
                                <span className="text-sm text-slate-600 font-medium">{item.systemStock}</span>
                                <span className="text-xs text-slate-400 ml-1">{item.uomSymbol}</span>
                              </div>
                              <div className="col-span-3 text-right">
                                <span className="text-sm text-slate-500">₹{item.unitCost.toLocaleString('en-IN')}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {selectedItemIds.size === 0
            ? 'Select items to begin counting'
            : `${selectedItemIds.size} item${selectedItemIds.size > 1 ? 's' : ''} selected for counting`
          }
        </p>
        <button onClick={onNext} disabled={selectedItemIds.size === 0}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Start Counting <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// STEP 2: COUNT STOCK — Table Layout with Clear Headers
// ══════════════════════════════════════════════════════════════════
function Step2_CountStock({
  selectedItems, countData, countedCount,
  updateCount, stepCount,
  onBack, onNext, onReset,
  collapsedCategories, toggleCollapse,
}) {
  const total = selectedItems.length;
  const progressPct = total > 0 ? Math.round((countedCount / total) * 100) : 0;

  // Group by category
  const grouped = {};
  selectedItems.forEach((item) => {
    if (!grouped[item.categoryName]) grouped[item.categoryName] = [];
    grouped[item.categoryName].push(item);
  });
  const sortedCats = Object.keys(grouped).sort();

  return (
    <div className="space-y-4">
      {/* Progress Bar */}
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-slate-700">
            Counting Progress: <span className="text-teal-600 font-semibold">{countedCount}</span> of {total} items
          </p>
          <p className="text-sm font-semibold text-teal-600">{progressPct}%</p>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2.5">
          <div className="bg-teal-500 h-2.5 rounded-full transition-all duration-500 ease-out" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Count Table by Category */}
      {sortedCats.map((cat) => {
        const items = grouped[cat];
        const collapsed = collapsedCategories.has(cat);
        const catCounted = items.filter((i) => countData[i.id]?.actualCount !== undefined && countData[i.id]?.actualCount !== '').length;

        return (
          <div key={cat} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Category Header */}
            <button onClick={() => toggleCollapse(cat)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-200">
              <div className="flex items-center gap-2">
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                <h3 className="font-semibold text-slate-900">{cat}</h3>
                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{items.length} items</span>
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${catCounted === items.length ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                {catCounted}/{items.length} counted
              </span>
            </button>

            {!collapsed && (
              <div className="overflow-x-auto">
                {/* Column Headers */}
                <div className="grid grid-cols-12 gap-2 items-center px-5 py-2.5 bg-slate-50 border-b border-slate-100">
                  <div className="col-span-3">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Item Name</span>
                  </div>
                  <div className="col-span-2 text-center">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">System Stock</span>
                  </div>
                  <div className="col-span-3 text-center">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Actual Count</span>
                  </div>
                  <div className="col-span-2 text-center">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Variance</span>
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Cost Impact</span>
                  </div>
                </div>

                {/* Item Rows */}
                <div className="divide-y divide-slate-100">
                  {items.map((item) => {
                    const d = countData[item.id];
                    const actualCount = d?.actualCount;
                    const isCounted = actualCount !== undefined && actualCount !== '';
                    const variance = isCounted ? actualCount - item.systemStock : null;
                    const variancePct = isCounted && item.systemStock > 0 ? (variance / item.systemStock) * 100 : null;
                    const absVariancePct = variancePct !== null ? Math.abs(variancePct) : 0;
                    const costImpact = isCounted ? Math.round(variance * item.unitCost) : null;
                    const isPositive = variance > 0;
                    const isNegative = variance < 0;
                    const isHighVariance = absVariancePct > 25;
                    const isMediumVariance = absVariancePct > 10 && !isHighVariance;

                    return (
                      <div
                        key={item.id}
                        className={`grid grid-cols-12 gap-2 items-center px-5 py-3.5 transition-colors ${
                          isCounted
                            ? isHighVariance ? 'bg-red-50/40' : isMediumVariance ? 'bg-amber-50/40' : 'bg-teal-50/20'
                            : 'hover:bg-slate-50/50'
                        }`}
                      >
                        {/* Item Name */}
                        <div className="col-span-3">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900 text-sm">{item.name}</p>
                            {isHighVariance && <AlertCircle size={14} className="text-red-500 flex-shrink-0" />}
                            {isMediumVariance && <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />}
                          </div>
                        </div>

                        {/* System Stock */}
                        <div className="col-span-2 text-center">
                          <span className="text-sm font-semibold text-slate-600">{item.systemStock.toFixed(2)}</span>
                          <span className="text-xs text-slate-400 ml-1">{item.uomSymbol}</span>
                        </div>

                        {/* Actual Count — Stepper + Input */}
                        <div className="col-span-3 flex items-center justify-center gap-1.5">
                          <button onClick={() => stepCount(item.id, item.systemStock, -1)}
                            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200 transition-colors text-slate-600">
                            <Minus size={14} />
                          </button>
                          <input
                            type="number"
                            value={actualCount ?? ''}
                            onChange={(e) => updateCount(item.id, e.target.value)}
                            placeholder="——"
                            step="0.01"
                            min="0"
                            className={`w-20 px-2 py-1.5 text-center text-sm font-semibold border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all ${
                              !isCounted ? 'border-slate-200 text-slate-400 bg-white' : isHighVariance ? 'border-red-300 text-red-700 bg-red-50' : isMediumVariance ? 'border-amber-300 text-amber-700 bg-amber-50' : 'border-teal-300 text-teal-700 bg-teal-50'
                            }`}
                          />
                          <button onClick={() => stepCount(item.id, item.systemStock, 1)}
                            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200 transition-colors text-slate-600">
                            <Plus size={14} />
                          </button>
                          <span className="text-xs text-slate-400 w-6">{item.uomSymbol}</span>
                        </div>

                        {/* Variance */}
                        <div className="col-span-2 text-center">
                          {isCounted ? (
                            <div>
                              <p className={`text-sm font-semibold ${isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-slate-500'}`}>
                                {isPositive ? '+' : ''}{variance.toFixed(2)}
                              </p>
                              <p className={`text-[10px] ${absVariancePct > 25 ? 'text-red-500 font-semibold' : absVariancePct > 10 ? 'text-amber-500 font-medium' : 'text-slate-400'}`}>
                                {isPositive ? '+' : ''}{variancePct.toFixed(1)}%
                              </p>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </div>

                        {/* Cost Impact */}
                        <div className="col-span-2 text-right">
                          {costImpact !== null && variance !== 0 ? (
                            <span className={`text-sm font-medium ${costImpact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {costImpact > 0 ? '+' : ''}₹{Math.abs(costImpact).toLocaleString('en-IN')}
                            </span>
                          ) : isCounted ? (
                            <span className="text-xs text-teal-500 font-medium">Match</span>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Footer */}
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <ChevronLeft size={16} /> Back to Selection
          </button>
          <button onClick={onReset} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">
            <RotateCcw size={14} /> Reset All
          </button>
        </div>
        <button onClick={onNext} disabled={countedCount === 0}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Review Changes <Eye size={16} />
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// STEP 3: REVIEW & SUBMIT
// ══════════════════════════════════════════════════════════════════
function Step3_ReviewSubmit({
  changedItems, countData, reviewSummary, updateReason, updateNotes,
  onBack, onSubmit, onReset, isSubmitting, selectedItems,
}) {
  const unchangedCount = selectedItems.length - changedItems.length;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Items with Variance</p>
          <p className="text-2xl font-bold text-slate-900">{reviewSummary.itemsWithVariance}</p>
          {unchangedCount > 0 && <p className="text-xs text-slate-400 mt-1">{unchangedCount} items matched system stock</p>}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Stock Found (Extra)</p>
          <p className="text-2xl font-bold text-green-600">+₹{reviewSummary.totalPositive.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Shrinkage / Loss</p>
          <p className="text-2xl font-bold text-red-600">-₹{Math.abs(reviewSummary.totalNegative).toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Net Impact Banner */}
      <div className={`rounded-xl px-5 py-4 border-2 ${reviewSummary.netCost >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Net Cost Impact</p>
            <p className={`text-3xl font-bold ${reviewSummary.netCost >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {reviewSummary.netCost >= 0 ? '+' : ''}₹{Math.abs(reviewSummary.netCost).toLocaleString('en-IN')}
            </p>
          </div>
          {reviewSummary.netCost < 0 && (
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
          )}
          {reviewSummary.netCost >= 0 && (
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle2 size={24} className="text-green-600" />
            </div>
          )}
        </div>
      </div>

      {/* Changed Items Table */}
      {changedItems.length > 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Items to Adjust ({changedItems.length})</h3>
            <p className="text-xs text-slate-500 mt-0.5">Select a reason for each item before submitting</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Item Name</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">System Stock</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-8"></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actual Count</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Variance</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Cost Impact</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-40">Reason <span className="text-red-500">*</span></th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-36">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {changedItems.map((item) => {
                  const d = countData[item.id];
                  const variance = d.actualCount - item.systemStock;
                  const variancePct = item.systemStock > 0 ? Math.abs((variance / item.systemStock) * 100) : 0;
                  const cost = Math.round(variance * item.unitCost);
                  const isHigh = variancePct > 25;
                  const isMedium = variancePct > 10 && !isHigh;

                  return (
                    <tr key={item.id} className={`transition-colors ${isHigh ? 'bg-red-50/40' : isMedium ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {isHigh && <AlertCircle size={14} className="text-red-500 flex-shrink-0" />}
                          {isMedium && <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />}
                          <div>
                            <span className="font-medium text-slate-900">{item.name}</span>
                            <span className="text-xs text-slate-400 block">{item.categoryName}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-slate-500">{item.systemStock.toFixed(2)} {item.uomSymbol}</td>
                      <td className="px-4 py-3 text-center"><ArrowRight size={14} className="text-slate-300 mx-auto" /></td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-slate-900">{d.actualCount.toFixed(2)} {item.uomSymbol}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-semibold ${variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                        </span>
                        <span className={`text-xs block ${isHigh ? 'text-red-500 font-semibold' : isMedium ? 'text-amber-500' : 'text-slate-400'}`}>
                          {variancePct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-medium ${cost > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {cost > 0 ? '+' : ''}₹{Math.abs(cost).toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <select value={d?.reason || ''} onChange={(e) => updateReason(item.id, e.target.value)}
                          className={`w-full px-2 py-1.5 text-xs border rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${!d?.reason ? 'border-red-300' : 'border-slate-200'}`}>
                          <option value="">Select reason...</option>
                          {REASON_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input type="text" value={d?.notes || ''} onChange={(e) => updateNotes(item.id, e.target.value)} placeholder="Optional notes"
                          className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 size={28} className="text-green-600" />
          </div>
          <p className="font-semibold text-slate-900 mb-1">All counts match system stock</p>
          <p className="text-sm text-slate-500">No adjustments needed</p>
        </div>
      )}

      {/* Footer */}
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <ChevronLeft size={16} /> Back to Counting
          </button>
          <button onClick={onReset} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">
            <RotateCcw size={14} /> Start Over
          </button>
        </div>
        <button onClick={onSubmit} disabled={changedItems.length === 0 || isSubmitting}
          className="inline-flex items-center gap-1.5 px-6 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Submitting...</>
          ) : (
            <><Send size={14} /> Confirm & Submit ({changedItems.length})</>
          )}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ADJUSTMENT HISTORY TAB
// ══════════════════════════════════════════════════════════════════
function AdjustmentHistoryTab({ history, search, setSearch, reason, setReason, onSort, SortIcon }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Filter Bar */}
      <div className="px-5 py-3 border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by item name or user..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
          </div>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
            <option value="">All Reasons</option>
            {REASON_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {/* <div className="flex items-center gap-2">
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
            <span className="text-slate-400 text-sm">to</span>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
          </div> */}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none" onClick={() => onSort('createdAt')}>
                <div className="flex items-center gap-1">Date & Time <SortIcon column="createdAt" /></div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none" onClick={() => onSort('itemName')}>
                <div className="flex items-center gap-1">Item Name <SortIcon column="itemName" /></div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Previous Stock</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">New Stock</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none" onClick={() => onSort('variance')}>
                <div className="flex items-center gap-1 justify-end">Variance <SortIcon column="variance" /></div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none" onClick={() => onSort('costImpact')}>
                <div className="flex items-center gap-1 justify-end">Cost Impact <SortIcon column="costImpact" /></div>
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Reason</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Adjusted By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {history.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3"><CheckCircle2 size={24} className="text-slate-400" /></div>
                  <p className="text-slate-900 font-medium mb-1">No adjustments found</p>
                  <p className="text-sm text-slate-500">Try adjusting your filters</p>
                </div>
              </td></tr>
            ) : (
              history.map((h) => {
                const variancePct = h.previousStock > 0 ? Math.abs((h.variance / h.previousStock) * 100) : 0;
                return (
                  <tr key={h.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-900">{new Date(h.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      <p className="text-xs text-slate-400">{new Date(h.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                    </td>
                    <td className="px-4 py-3"><span className="font-medium text-slate-900">{h.itemName}</span></td>
                    <td className="px-4 py-3 text-right text-sm text-slate-500">{h.previousStock.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">{h.newStock.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <div className="w-10 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${h.variance > 0 ? 'bg-green-400' : 'bg-red-400'}`}
                            style={{ width: `${Math.min(variancePct, 100)}%` }} />
                        </div>
                        <span className={`text-sm font-semibold ${h.variance > 0 ? 'text-green-600' : h.variance < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                          {h.variance > 0 ? '+' : ''}{h.variance.toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-medium ${h.costImpact > 0 ? 'text-green-600' : h.costImpact < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                        {h.costImpact > 0 ? '+' : ''}₹{Math.abs(h.costImpact).toLocaleString('en-IN')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${REASON_BADGE_COLORS[h.reason] || 'bg-slate-100 text-slate-700'}`}>
                        {REASON_LABELS[h.reason] || h.reason}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{h.adjustedBy}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
